<template>
    <form v-if="status.detail" @submit.prevent="submitForm">
        <div class="row">
            <div class="col-md-6">
                <b-form-group :label="$t('label.code')">
                    <b-form-input v-model.trim="form.code" readonly />
                    <b-form-text v-if="form.type === 0">{{ $t('label.optionalCustomNotificationCode') }}</b-form-text>
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group :label="$t('label.name')">
                    <b-form-input v-model.trim="form.name" readonly />
                </b-form-group>
            </div>
        </div>

        <b-form-group :label="$t('label.title')">
            <b-form-input v-model.trim="form.title" readonly />
        </b-form-group>

        <b-form-group :label="$t('label.shortDescription')">
            <b-form-textarea v-model="form.description" rows="2" readonly />
        </b-form-group>

        <b-form-group :label="$t('label.longDescription')">
            <b-form-textarea v-model="form.longDescription" rows="3" readonly />
            <b-form-text>{{ $t('label.templateVariablesSupported') }} <code v-pre>{{title}}</code></b-form-text>
        </b-form-group>

        <div class="row">
            <div class="col-md-6">
                <b-form-group :label="$t('label.notificationType')">
                    <b-form-input :value="typeName(form.type)" disabled />
                    <b-form-text>{{ $t('label.notificationTypeLocked') }}</b-form-text>
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group :label="$t('label.icon')">
                    <multiselect v-model="form.icon" track-by="value" label="text" :placeholder="$t('label.selectIcon')"
                        :options="notificationIconDdl" :searchable="true" :allow-empty="true" :disabled="true" />
                    <b-form-text>{{ $t('label.notificationIconAppHint') }}</b-form-text>
                </b-form-group>
            </div>
        </div>

        <div class="row">
            <div class="col-md-6">
                <b-form-group :label="$t('label.redirectPage')">
                    <b-form-input v-model.trim="form.redirectPage" :placeholder="$t('label.optional')" readonly />
                    <b-form-text>
                        {{ $t('label.redirectPageHint') }}
                    </b-form-text>
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group :label="$t('label.redirectPageParameters')">
                    <b-form-tags v-model="form.redirectPageParameters" separator=", "
                        :placeholder="$t('label.addParameter')" disabled />
                    <b-form-text>
                        {{ $t('label.redirectParameterHint') }}
                    </b-form-text>
                </b-form-group>
            </div>
        </div>

        <b-form-group :label="$t('label.notificationStatus')">
            <b-form-checkbox v-model="form.isActive" switch>
                {{ $t(form.isActive ? 'label.activeNotificationHint' : 'label.inactiveNotificationHint') }}
            </b-form-checkbox>
            <b-form-text>{{ $t('label.disableNotificationHint') }}</b-form-text>
        </b-form-group>

        <b-form-group :label="$t('label.channels')">
            <b-form-checkbox v-for="channel in notificationChannelDdl" :key="channel.value" v-model="form.channelUUIDs"
                :value="channel.value" @change="channelSelectionChanged">
                {{ channel.text }}
            </b-form-checkbox>
            <b-form-text v-if="!notificationChannelDdl.length">{{ $t('label.noNotificationChannels') }}</b-form-text>
            <b-alert v-if="showFcmHint" show variant="info" class="mt-2 mb-0 py-2">
                <i class="mdi mdi-information-outline mr-1"></i>
                {{ $t('label.pushNotificationAppHint') }}
            </b-alert>
        </b-form-group>

        <template v-if="showEmailFields">
            <b-form-group :label="$t('label.emailSubject')">
                <b-form-input v-model.trim="form.emailSubject" readonly />
            </b-form-group>

            <b-form-group :label="$t('label.emailHtmlContent')">
                <b-form-textarea v-model="form.emailHtmlContent" rows="6" readonly />
                <b-form-text>{{ $t('label.emailFallbackHint') }}</b-form-text>
            </b-form-group>
        </template>

        <div v-if="showWhatsAppFields" class="row">
            <div class="col-md-6">
                <b-form-group :label="$t('label.whatsappTemplateName')">
                    <b-form-input v-model.trim="form.whatsappTemplateName" readonly />
                    <b-form-text>
                        {{ $t('label.whatsappTemplateHint') }}
                    </b-form-text>
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group :label="$t('label.whatsappTemplateLanguage')">
                    <b-form-input value="en" disabled />
                    <b-form-text>{{ $t('label.whatsappLanguageHint') }}</b-form-text>
                </b-form-group>
            </div>
        </div>

        <hr class="my-3 mx-n3">
        <div class="d-flex justify-content-end">
            <b-button type="button" variant="danger" class="mr-2" @click="$emit('done')">
                {{ $t('label.cancel') }}
            </b-button>
            <b-button type="submit" variant="info" :disabled="!status.save">
                <span v-if="status.save">{{ $t('label.saveSettings') }}</span>
                <b-spinner v-else small />
            </b-button>
        </div>
    </form>
    <div v-else class="d-flex justify-content-center p-5">
        <b-spinner variant="info" />
    </div>
</template>

<script>
import { mapState } from 'vuex';
import Multiselect from 'vue-multiselect';
import { notificationMethods, lookupMethods } from '@/state/api';
import { NOTIFICATION_MODULE } from '@/state/api/notification.state';

export default {
    name: 'NotificationForm',
    components: { Multiselect },
    props: {
        notificationUUID: { type: String, default: '' },
        searchCriteria: { type: Object, required: true },
    },
    data() {
        return {
            showEmailFields: false,
            showWhatsAppFields: false,
            showFcmHint: false,
            form: this.defaultForm(),
        };
    },
    computed: {
        ...mapState(NOTIFICATION_MODULE, ['status']),
        details() {
            return this.$store.state.api.notification.details || {};
        },
        notificationIconDdl() {
            return this.$store.state.api.lookup.notificationIconDdl || [];
        },
        notificationChannelDdl() {
            return this.$store.state.api.lookup.notificationChannelDdl || [];
        },
    },
    async created() {
        await Promise.all([this.lookupNotificationIcon(), this.lookupNotificationChannel()]);
        await this.loadNotification();
    },
    methods: {
        ...notificationMethods,
        ...lookupMethods,
        defaultForm() {
            return {
                uuid: '', code: '', name: '', title: '', longDescription: '', description: '',
                emailSubject: '', emailHtmlContent: '', type: 0, redirectPage: '',
                redirectPageParameters: [], icon: null, channelUUIDs: [], whatsappTemplateName: '',
                whatsappTemplateLanguage: 'en', whatsappTemplateParameterKeys: [], isActive: true,
            };
        },
        async loadNotification() {
            if (!this.notificationUUID) return;
            const result = await this.getNotification({ uuid: this.notificationUUID });
            if (result) {
                this.form = {
                    ...this.defaultForm(),
                    ...this.details,
                    whatsappTemplateLanguage: 'en',
                    icon: this.optionFor(this.notificationIconDdl, this.details.iconUUID, this.details.icon),
                };
            }
            await this.$nextTick();
            this.syncChannelFields();
        },
        optionFor(options, uuid, record) {
            return options.find((option) => option.value === uuid) || (uuid ? {
                value: uuid,
                text: record?.name || uuid,
            } : null);
        },
        typeName(type) {
            return type === 1 ? this.$t('label.system') : this.$t('label.custom');
        },
        channelSelectionChanged() {
            this.$nextTick(() => this.syncChannelFields());
        },
        syncChannelFields() {
            const selectedChannels = this.notificationChannelDdl.filter(
                (channel) => this.form.channelUUIDs.includes(channel.value),
            );
            this.showEmailFields = selectedChannels.some((channel) => this.channelMatches(channel, 'EMAIL'));
            this.showWhatsAppFields = selectedChannels.some(
                (channel) => this.channelMatches(channel, 'WHATSAPP') || this.channelMatches(channel, 'WA'),
            );
            this.showFcmHint = selectedChannels.some(
                (channel) => this.channelMatches(channel, 'FCM') || this.channelMatches(channel, 'PUSHNOTIFICATION'),
            );
        },
        channelMatches(channel, expected) {
            return [channel.code, channel.text]
                .filter(Boolean)
                .map((value) => String(value).toUpperCase().replace(/[\s_-]/g, ''))
                .some((value) => expected === 'WA' ? value === expected : value.includes(expected));
        },
        async submitForm() {
            const data = {
                ...this.form,
                iconUUID: this.form.icon?.value || this.form.iconUUID || null,
            };
            const result = await this.updateNotification({ data, filter: this.searchCriteria });
            if (result?.success) this.$emit('done');
        },
    },
};
</script>
